import { useLayoutEffect, type RefObject } from "react";

const CLEARANCE_VAR = "--bottom-nav-opaque-height";

// Shells can stack (an overlay's dock over the page's own) and they share one CSS var, so the
// tallest mounted shell wins: the reserved clearance never under-reserves for whichever shell is
// actually on screen, independent of mount order. Unmounting republishes from the survivors;
// clearing would strip the clearance while a shell is still visible.
const mountedShells = new Set<HTMLElement>();

function publishClearance(): void {
  if (mountedShells.size === 0) {
    document.documentElement.style.removeProperty(CLEARANCE_VAR);
    return;
  }
  const tallest = Math.max(...[...mountedShells].map((shell) => shell.offsetHeight));
  document.documentElement.style.setProperty(CLEARANCE_VAR, `${tallest}px`);
}

/**
 * Publishes `--bottom-nav-opaque-height` on the document root: the bottom clearance page content
 * must reserve to scroll clear of the fixed shells. Read from CSS, never from React, so the value
 * deliberately lives outside render state.
 */
export function useBottomNavClearance(shellRef: RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;

    mountedShells.add(shell);
    publishClearance();

    const observer = new ResizeObserver(publishClearance);
    observer.observe(shell);

    return () => {
      observer.disconnect();
      mountedShells.delete(shell);
      publishClearance();
    };
  }, [shellRef]);
}
